import React, { useCallback, useMemo, useState, useEffect } from 'react';
import {
  computeFieldFrame,
  FieldPresentation,
  ListingResource,
  useFieldRemoveFromList,
  useListingField,
  useListingFieldWithFrames,
  registerFieldFrame,
  unregisterFieldFrame,
  FieldFrameData,
} from '@brightmls/nextgen.lae.flc';
import { PropertyField } from '../../../system/field';
import {
  ControlField,
  FieldMetadata,
  useMetadata,
} from '../../../contexts/MetadataContext';
import {
  FormFieldState,
  Option,
} from '../../../components/edit/FormFieldContext';
import { convertFieldValue } from '../../../utils/fieldUtils';
import CustomControl from '../../../components/common/CustomControl';
import Field from '../../../components/edit/Field';
import { useInlineAlert } from '../../../components/edit/hooks/useInlineAlert';
import { ResourceField } from '../../../components/edit/hooks/useResourceForm/useResourceForm';

export interface MemoizedFieldContextProps {
  getFieldValue: (key: string, fallback: any) => any;
  updateField: (key: string, value: any) => void;
  getFieldPresentation: (
    resourceType: ListingResource,
    key: string,
  ) => FieldPresentation;
  getRemoveFromList: (resourceType: ListingResource, key: string) => string[];
  getFieldMetadata: (key: string) => FieldMetadata;
  isFieldHidden: (resourceType: ListingResource, fieldName: string) => boolean;
  resourceType: ListingResource;
}

interface MemoizedFieldProps {
  field: ControlField;
  showLabel?: boolean;
  fieldContext?: MemoizedFieldContextProps;
  fieldFramePrefix?: string;
  onCustomControlLoaded: (fieldKey: string) => void;
}

export const MemoizedField: React.FC<MemoizedFieldProps> = React.memo(
  ({
    field,
    showLabel = true,
    fieldContext,
    fieldFramePrefix,
    onCustomControlLoaded,
  }) => {
    const { field: fieldMetaGetter, lookup, getHelpText } = useMetadata();
    const { inlineAlert, getAlertResponse, hasInlineAlert } = useInlineAlert(
      field.Key,
    );

    // Track if field has been touched
    const [isTouched, setIsTouched] = useState(false);

    // Get field metadata
    const rawFieldMeta = fieldMetaGetter(field.Key);
    const rawHelpText = getHelpText(rawFieldMeta?.ResourceType, field.Key);

    // Get computed metadata
    const fieldMeta = useMemo(() => {
      const resourceField = field as ResourceField;
      if (resourceField.Metadata) {
        return resourceField.Metadata as FieldMetadata;
      }
      if (rawHelpText) {
        return {
          ...rawFieldMeta,
          HelpText: rawHelpText,
        };
      }
      return rawFieldMeta;
    }, [field, rawFieldMeta, fieldContext]);

    const fieldKey = field.Key;

    // Get field source meta
    const sourceFieldMeta = useMemo(() => {
      if (field.SourceKey) {
        return fieldMetaGetter(field.SourceKey);
      }
      // Try to get _SOURCE field
      return fieldMetaGetter(`${field.Key}_SOURCE`);
    }, [fieldMetaGetter, field.Key, field.SourceKey]);

    // Step 1: Get basic field data (removeFromList) without validation
    const fieldRemoveFromList = fieldContext
      ? null
      : useFieldRemoveFromList(ListingResource.Property, field.Key);

    // Step 2: Get removeFromList from either context or basic field data
    const removeFromList = fieldContext
      ? fieldContext.getRemoveFromList(fieldContext.resourceType, fieldKey)
      : fieldRemoveFromList?.removeFromList || [];

    // Step 3: Now compute filtered options with the removeFromList we have
    const filteredOptions: Option[] | undefined = useMemo(() => {
      const lookupKey = fieldMeta?.Lookup;
      const lookups = lookupKey ? lookup(lookupKey) : undefined;
      if (!lookups) return undefined;

      const options = lookups.map((item) => ({
        value: item.LookupValue,
        label: item.DisplayValue || item.LookupValue,
        description: item.Description as string,
        metadata: item,
      }));

      return options.filter((option) => !removeFromList.includes(option.value));
    }, [fieldMeta?.Lookup, lookup, removeFromList]);

    // Step 4: Now get the full field data with validation using filtered options
    const magicFieldData = fieldContext
      ? null
      : useListingFieldWithFrames(
          ListingResource.Property,
          fieldKey as PropertyField,
          '', // fallback value
          {
            metadata: fieldMeta,
            trackRenders: true,
            isTouched,
            field,
            validOptions: filteredOptions,
          },
        );

    // Magic hook for source field value (if we have a source field)
    const sourceFieldData =
      !fieldContext && sourceFieldMeta
        ? useListingField(
            ListingResource.Property,
            sourceFieldMeta.Key as PropertyField,
            '', // fallback value
            {
              metadata: sourceFieldMeta,
              trackRenders: false, // Don't track renders for source field
            },
          )
        : null;

    // Get fieldSourceValue
    const fieldSourceValue = useMemo(() => {
      if (!sourceFieldMeta) {
        return ''; // No source field, use regular value
      }

      if (fieldContext) {
        return fieldContext.getFieldValue(sourceFieldMeta.Key, '');
      } else if (sourceFieldData) {
        return sourceFieldData.value;
      }

      return '';
    }, [sourceFieldMeta, fieldContext, sourceFieldData]);

    // Compute field frame when using context
    const contextFieldFrame = useMemo(() => {
      if (!fieldContext || !fieldMeta) return null;

      const rawValue = fieldContext.getFieldValue(
        fieldKey as PropertyField,
        '',
      );
      const presentation = fieldContext.getFieldPresentation(
        ListingResource.Property,
        fieldKey,
      );

      // Create a getFieldValue function for validation
      const getFieldValue = (fieldName: string, fallback: any = '') => {
        return fieldContext.getFieldValue(fieldName, fallback);
      };

      // Use the utility function to compute frame
      return computeFieldFrame(
        rawValue,
        isTouched,
        field,
        presentation,
        fieldMeta,
        filteredOptions,
        getFieldValue,
        false, // isLocked - you might want to get this from context
      );
    }, [fieldContext, fieldMeta, fieldKey, isTouched, field, filteredOptions]);

    // Get values either from magic hook or context
    const fieldValue = fieldContext
      ? fieldContext.getFieldValue(fieldKey as PropertyField, '')
      : magicFieldData?.value;

    const fieldFrame = fieldContext
      ? contextFieldFrame!
      : magicFieldData?.fieldFrame!;

    const presentation = fieldContext
      ? fieldContext.getFieldPresentation(ListingResource.Property, fieldKey)
      : magicFieldData?.presentation!;

    const isHidden = fieldContext
      ? fieldContext.isFieldHidden(ListingResource.Property, fieldKey)
      : magicFieldData?.isHidden;

    // Register field frame with the form store
    useEffect(() => {
      if (fieldFrame && fieldMeta) {
        const resourceType =
          fieldContext?.resourceType || ListingResource.Property;
        const fullFieldKey = `${fieldFramePrefix ? fieldFramePrefix + '.' : ''}${resourceType}.${fieldKey}`;

        // Convert field frame to the format expected by the registry
        const frameData: FieldFrameData = {
          message: fieldFrame.message || '',
          errorMessage: fieldFrame.errorMessage || '',
          warningMessage: fieldFrame.warningMessage || '',
          isError: fieldFrame.isError || false,
          isMessage: fieldFrame.isMessage || false,
          isWarning: fieldFrame.isWarning || false,
          isRequired: fieldFrame.isRequired || false,
          isDisabled: fieldFrame.isDisabled || false,
          isReadOnly: fieldFrame.isReadOnly || false,
          isTouched: fieldFrame.isTouched || false,
        };

        registerFieldFrame(fullFieldKey, frameData);

        // Cleanup on unmount or when field key changes
        return () => {
          unregisterFieldFrame(fullFieldKey);
        };
      }
    }, [fieldFrame]);

    // Handle field value changes - use magicFieldData for updates, not basicFieldData
    const handleFieldValueChange = useCallback(
      (newValue: any) => {
        const convertedValue = convertFieldValue(newValue, fieldMeta);

        if (fieldContext) {
          fieldContext.updateField(fieldKey as PropertyField, convertedValue);
        } else {
          magicFieldData?.updateValue(convertedValue);
        }

        if (!isTouched) {
          setIsTouched(true);
        }
      },
      [fieldContext, magicFieldData, fieldMeta, fieldKey, isTouched],
    );

    // Inline alert changes
    const handleChangeAfterAlert = useCallback(
      async (value: any) => {
        const isNullValue = value === null || value === undefined;

        if (!isNullValue) {
          const shouldContinueWithValue = await getAlertResponse(value);
          if (!shouldContinueWithValue) {
            return;
          }
        }

        return handleFieldValueChange(value);
      },
      [getAlertResponse, handleFieldValueChange],
    );

    // Handle field blur/touch
    const handleFieldTouch = useCallback(() => {
      setIsTouched(true);
    }, []);

    // Don't render if field is hidden
    if (isHidden) {
      return null;
    }

    const fieldLabel = field.Label || fieldMeta?.Name;
    const fieldDisplayValue = filteredOptions
      ? filteredOptions.find((o) => o.value === fieldValue)?.label || fieldValue
      : fieldValue;

    // If the field is a multi-select, and was not assigned a custom control: use the default multi-select control
    if (fieldMeta?.MultiSelect && !field.Control) {
      field.Control = 'MultiSelectField';
    }

    // Check if this is a custom control
    if (field.Control) {
      const handleCustomControlLoaded = useCallback(() => {
        onCustomControlLoaded(field.Key);
      }, [onCustomControlLoaded, field.Key]);

      return (
        <CustomControl
          field={field}
          control={field.Control}
          options={filteredOptions}
          fieldMeta={fieldMeta}
          isTouched={isTouched}
          fieldLabel={fieldLabel}
          fieldPresentation={presentation}
          fieldValue={fieldValue}
          fieldDisplayValue={fieldDisplayValue}
          resourceFormContext={fieldContext}
          fieldSourceValue={fieldSourceValue}
          fieldFrame={fieldFrame as FormFieldState} // Pass the frame!
          handleFieldTouch={handleFieldTouch}
          onFieldValueChange={
            hasInlineAlert() ? handleChangeAfterAlert : handleFieldValueChange
          }
          onLoaded={handleCustomControlLoaded}
        />
      );
    }

    // Don't render if no metadata
    if (!fieldMeta) {
      return null;
    }

    return (
      <>
        <Field
          field={field}
          fieldMeta={fieldMeta}
          fieldLabel={fieldLabel}
          fieldDisplayValue={fieldDisplayValue}
          fieldPresentation={presentation}
          options={filteredOptions}
          onFieldValueChange={
            hasInlineAlert() ? handleChangeAfterAlert : handleFieldValueChange
          }
          handleFieldTouch={handleFieldTouch}
          fieldValue={fieldValue}
          fieldSourceValue={fieldSourceValue}
          fieldFrame={fieldFrame as FormFieldState} // Pass the computed frame!
          isTouched={isTouched}
          showLabel={showLabel}
          resourceFormContext={fieldContext}
        />
        {inlineAlert()}
      </>
    );
  },
);

MemoizedField.displayName = 'MemoizedField';

export default MemoizedField;
